/**
 * Feature-flag + scope helpers for the AccessPoint migration monitor.
 *
 * The migration monitor talks to internal backend fields/args (`isStaging`,
 * `scope`) that only exist on newer backend deployments. To avoid breaking old
 * CRM sessions when the backend/frontend are mismatched, the whole monitor is
 * gated behind a conservative env flag and fails closed when disabled.
 */

/** Selected AccessPoint scope for a migration/status run. */
export type MigrationScopeOption = 'STAGING' | 'PRODUCTION' | 'ALL';

/** Default scope: staging only, so an accidental run never touches production. */
export const DEFAULT_MIGRATION_SCOPE: MigrationScopeOption = 'STAGING';

/**
 * Whether the migration monitor is enabled. Fails closed: only the explicit
 * string 'true' turns it on.
 */
export const isAccessPointMigrationMonitorEnabled = (): boolean => process.env.REACT_APP_ENABLE_ACCESS_POINT_MIGRATION_MONITOR === 'true';

/**
 * Map a UI scope selection to the optional `isStaging` GraphQL arg:
 * - STAGING -> true
 * - PRODUCTION -> false
 * - ALL -> undefined (omit the arg so the backend processes both)
 */
export const scopeToIsStaging = (scope: MigrationScopeOption): boolean | undefined => {
  if (scope === 'STAGING') return true;
  if (scope === 'PRODUCTION') return false;
  return undefined;
};

/** Human-readable label for a scope selection. */
export const scopeLabel = (scope: MigrationScopeOption): string => {
  if (scope === 'STAGING') return 'Nur Staging';
  if (scope === 'PRODUCTION') return 'Nur Produktion';
  return 'Alle AccessPoints';
};

/** Normalize a backend `scope`/`isStaging` pair into a readable label. */
export const describeReturnedScope = (scope?: string | null, isStaging?: boolean | null): string => {
  if (scope === 'STAGING' || (scope == null && isStaging === true)) return scopeLabel('STAGING');
  if (scope === 'PRODUCTION' || (scope == null && isStaging === false)) return scopeLabel('PRODUCTION');
  if (scope === 'ALL' || (scope == null && (isStaging === null || isStaging === undefined))) return scopeLabel('ALL');
  return scope ?? 'Unbekannt';
};
